import { format, parseISO } from 'date-fns'
import { ChevronDown } from 'lucide-react'
import { useState } from 'react'

import { cn } from '@/app/utils/cn'
import { Skeleton } from '@/view/components/ui/skeleton'

import { AttendanceChart } from './AttendanceChart'
import { useSupportAttendanceStats } from './useSupportAttendanceStats'

interface IndicatorProps {
  label: string
  value: number
  hint?: string
  highlight?: boolean
}

const formatNumber = (value: number) =>
  value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })

const Indicator = ({ label, value, hint, highlight }: IndicatorProps) => (
  <div
    className={cn(
      'flex min-w-0 flex-col gap-1 rounded-lg border border-gray01 bg-white px-4 py-3',
      highlight && 'border-primary',
    )}
  >
    <span className="text-xs font-medium leading-tight text-black04">
      {label}
    </span>
    <span
      className={cn(
        'text-2xl font-semibold',
        highlight ? 'text-primary' : 'text-black01',
      )}
    >
      {formatNumber(value)}
    </span>
    {hint && (
      <span className="text-[11px] leading-tight text-black04">{hint}</span>
    )}
  </div>
)

interface SupportAttendanceStatsProps {
  className?: string
}

const COLLAPSED_STORAGE_KEY = 'support:attendanceStatsCollapsed'

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

export const SupportAttendanceStats = ({
  className,
}: SupportAttendanceStatsProps) => {
  const { data, isLoading } = useSupportAttendanceStats()
  const [collapsed, setCollapsed] = useState(readCollapsed)

  const toggleCollapsed = () => {
    const next = !collapsed
    setCollapsed(next)
    try {
      localStorage.setItem(COLLAPSED_STORAGE_KEY, next ? '1' : '0')
    } catch {}
  }

  if (isLoading) {
    return <Skeleton className={cn('h-[300px] w-full rounded-xl', className)} />
  }

  if (!data) return null

  const { indicators } = data
  const period = `${format(parseISO(data.from), 'dd/MM')} a ${format(parseISO(data.to), 'dd/MM')}`

  return (
    <section
      className={cn(
        'flex flex-col gap-4 rounded-xl border bg-card p-4',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">
            Indicadores de chamados
          </span>
          <span className="text-xs text-black04">
            Últimos 7 dias · {period}
          </span>
        </div>
        <button
          type="button"
          onClick={toggleCollapsed}
          aria-expanded={!collapsed}
          className="flex shrink-0 items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-black04 hover:bg-gray02"
        >
          {collapsed ? 'Mostrar' : 'Ocultar'}
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform',
              !collapsed && 'rotate-180',
            )}
          />
        </button>
      </div>

      {!collapsed && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Indicator
              label="Atendidos por você"
              value={indicators.attended}
              highlight
            />
            <Indicator
              label="Média por atendente"
              value={indicators.averageAttended}
              hint={`${indicators.attendants} ${indicators.attendants === 1 ? 'atendente' : 'atendentes'} no período`}
            />
            <Indicator label="Abertos no período" value={indicators.opened} />
            <Indicator
              label="Aguardando atendimento"
              value={indicators.openNow}
              hint={`${indicators.inProgressNow} em atendimento agora`}
            />
          </div>

          <AttendanceChart days={data.days} barLabel="Atendidos por você" />
        </>
      )}
    </section>
  )
}
